import * as LocalStorage from "@omni/utils/localStorage.utils";

import { MAX_WISHLIST_ITEMS } from "@/components/Wishlist/wishlist.config";
import { WishlistModel } from "@/redux/modules/api/data/wishlist/@types/wishlist.model";
import { translateKey } from "@/redux/modules/value/translations/translations.helpers";
import { TTranslation } from "@/redux/modules/value/translations/translations.types";
import { StorageKeys } from "@/utils/enums";

const LOCAL_WISHLIST_KEY = StorageKeys.LocalWishlist;

type LocalWishlistItem = {
    sku: string;
};

type LocalWishlist = Array<LocalWishlistItem>;

const parseEntries = (array: LocalWishlist): LocalWishlist => {
    return array.slice(0, MAX_WISHLIST_ITEMS);
};

const isFull = (wishlist: LocalWishlist): boolean =>
    wishlist.length >= MAX_WISHLIST_ITEMS;

const addProductDirectly = (
    wishlist: LocalWishlist,
    sku: string
): LocalWishlist => {
    const newWishlist = [{ sku: sku }, ...wishlist];
    LocalStorage.setItem(LOCAL_WISHLIST_KEY, JSON.stringify(newWishlist));

    return newWishlist;
};

export const isWishlistFull = () => isFull(getLocalWishlist());

export const hasLocalWishList = () =>
    !!LocalStorage.getItem(LOCAL_WISHLIST_KEY);

export const createLocalWishlist = (sku: string) => {
    const wishlist: LocalWishlist = [{ sku: sku }];
    LocalStorage.setItem(LOCAL_WISHLIST_KEY, JSON.stringify(wishlist));

    return wishlist;
};

export const addProductToLocalWishlist = (sku: string): LocalWishlist => {
    if (!hasLocalWishList()) {
        return createLocalWishlist(sku);
    }

    const wishlist = getLocalWishlist(sku);

    return isFull(wishlist) ? wishlist : addProductDirectly(wishlist, sku);
};

export const getLocalWishlist = (omitSku?: string): LocalWishlist => {
    const localStorageItem = LocalStorage.getItem(LOCAL_WISHLIST_KEY);
    if (!localStorageItem) {
        return [];
    } else {
        const localWishlistArray: LocalWishlist = JSON.parse(localStorageItem);
        if (omitSku) {
            return parseEntries(localWishlistArray).filter(
                (product: LocalWishlistItem) => product.sku !== omitSku
            );
        }

        return parseEntries(localWishlistArray);
    }
};

export const removeProductFromLocalWishlist = (sku: string): LocalWishlist => {
    const localStorageItem = LocalStorage.getItem(LOCAL_WISHLIST_KEY);

    if (!localStorageItem) {
        return [];
    }

    const localWishlistArray: LocalWishlist = JSON.parse(localStorageItem);
    const newArray = localWishlistArray.filter(
        (product) => product.sku !== sku
    );
    LocalStorage.setItem(LOCAL_WISHLIST_KEY, JSON.stringify(newArray));

    return newArray;
};

export const removeLocalWishlist = () => {
    if (LocalStorage.getItem(LOCAL_WISHLIST_KEY)) {
        LocalStorage.removeItem(LOCAL_WISHLIST_KEY);
    }
};

export const getNewName = (
    list: Array<WishlistModel>,
    translations: TTranslation
) => {
    const baseName =
        translateKey(translations, "wishlist.wishlistName") || "Verlanglijstje";
    if (list.length === 0) {
        return baseName;
    }

    const hasName = (name: string) => list.some((l) => l.name === name);

    const generate = (name: string, index: number): string => {
        const newName = `${name} - ${index}`;
        if (!hasName(newName)) {
            return newName;
        }

        return generate(baseName, index + 1);
    };

    return hasName(baseName) ? generate(baseName, 1) : baseName;
};
